/**
 * PHASE 1: EXTRUSION METADATA PRESERVATION SYSTEM
 * 
 * Fixes: Critical issue where parent-child relationships between main segments 
 * and connectors are broken during resize/split operations (16 instances detected)
 * 
 * Prevention Strategy: Implement robust metadata preservation during all extrusion operations.
 * Add validation checks before/after resize operations.
 */

import { Wall, Point, WallWithTempId } from '../types';

export interface ExtrusionMetadata {
  isMainExtrusionSegment?: boolean;
  isExtrusionConnector?: boolean;
  parentWallId?: string;
  mainSegmentId?: string;
  extrusionDepth?: number;
  extrusionDirection?: { x: number; y: number };
  connectorType?: 'start' | 'end';
  originalMainSegmentId?: string; // Track the original main segment for validation
}

export interface ExtrusionRelationship {
  mainSegmentId: string;
  connectorIds: string[];
  parentWallId?: string;
  isValid: boolean;
}

/**
 * Extracts and validates extrusion metadata from a wall
 */
export function extractExtrusionMetadata(wall: Wall): ExtrusionMetadata {
  const metadata: ExtrusionMetadata = {};
  const wallAny = wall as any;
  
  // Extract all possible extrusion-related metadata
  if (wallAny.isMainExtrusionSegment) metadata.isMainExtrusionSegment = true;
  if (wallAny.isExtrusionConnector) metadata.isExtrusionConnector = true;
  if (wallAny.parentWallId) metadata.parentWallId = wallAny.parentWallId;
  if (wallAny.mainSegmentId) metadata.mainSegmentId = wallAny.mainSegmentId;
  if (wallAny.extrusionDepth) metadata.extrusionDepth = wallAny.extrusionDepth;
  if (wallAny.extrusionDirection) metadata.extrusionDirection = wallAny.extrusionDirection;
  if (wallAny.connectorType) metadata.connectorType = wallAny.connectorType;
  if (wallAny.originalMainSegmentId) metadata.originalMainSegmentId = wallAny.originalMainSegmentId;
  
  return metadata;
}

/**
 * Preserves extrusion metadata when creating updated wall
 */
export function preserveExtrusionMetadata(
  newWall: Wall, 
  originalMetadata: ExtrusionMetadata
): Wall {
  const preservedWall = { ...newWall } as any;
  
  // Preserve all extrusion metadata
  if (originalMetadata.isMainExtrusionSegment) {
    preservedWall.isMainExtrusionSegment = true;
  }
  if (originalMetadata.isExtrusionConnector) {
    preservedWall.isExtrusionConnector = true;
  }
  if (originalMetadata.parentWallId) {
    preservedWall.parentWallId = originalMetadata.parentWallId;
  }
  if (originalMetadata.mainSegmentId) {
    preservedWall.mainSegmentId = originalMetadata.mainSegmentId;
  }
  if (originalMetadata.extrusionDepth !== undefined) {
    preservedWall.extrusionDepth = originalMetadata.extrusionDepth;
  }
  if (originalMetadata.extrusionDirection) {
    preservedWall.extrusionDirection = { ...originalMetadata.extrusionDirection };
  }
  if (originalMetadata.connectorType) {
    preservedWall.connectorType = originalMetadata.connectorType;
  }
  if (originalMetadata.originalMainSegmentId) {
    preservedWall.originalMainSegmentId = originalMetadata.originalMainSegmentId;
  }
  
  console.log('🔗 EXTRUSION_METADATA_PRESERVED', {
    wallId: (preservedWall as any).tempId,
    isMainSegment: preservedWall.isMainExtrusionSegment,
    isConnector: preservedWall.isExtrusionConnector,
    parentWallId: preservedWall.parentWallId,
    mainSegmentId: preservedWall.mainSegmentId
  });
  
  return preservedWall;
}

/**
 * Analyzes and validates extrusion relationships in a wall set
 */
export function analyzeExtrusionRelationships(walls: Wall[]): ExtrusionRelationship[] {
  const relationships: ExtrusionRelationship[] = [];
  const mainSegments = walls.filter(w => (w as any).isMainExtrusionSegment);
  
  for (const mainSegment of mainSegments) {
    const mainSegmentId = (mainSegment as any).tempId;
    if (!mainSegmentId) continue;
    
    // Find all connectors related to this main segment
    const relatedConnectors = walls.filter(w => {
      const wallAny = w as any;
      return wallAny.isExtrusionConnector && (
        wallAny.mainSegmentId === mainSegmentId ||
        wallAny.parentWallId === (mainSegment as any).parentWallId
      );
    });
    
    const connectorIds = relatedConnectors
      .map(c => (c as any).tempId)
      .filter(id => id);
    
    // Enhanced validation: Accept 1+ connectors for edge cases (e.g., boundary extrusions)
    // Most extrusions should have 2+ connectors, but some valid cases have only 1
    const isValid = connectorIds.length >= 1;
    
    const relationship: ExtrusionRelationship = {
      mainSegmentId,
      connectorIds,
      parentWallId: (mainSegment as any).parentWallId,
      isValid
    };
    
    relationships.push(relationship);
    
    // Detailed logging for validation analysis
    console.log('🔍 RELATIONSHIP_DETAILS', {
      mainSegmentId,
      connectorIds,
      connectorCount: connectorIds.length,
      isValid,
      relatedConnectors: relatedConnectors.map(c => ({
        tempId: (c as any).tempId,
        mainSegmentId: (c as any).mainSegmentId,
        parentWallId: (c as any).parentWallId,
        start: c.start,
        end: c.end
      }))
    });
  }
  
  console.log('🔍 EXTRUSION_RELATIONSHIPS_ANALYZED', {
    totalMainSegments: mainSegments.length,
    relationships: relationships.map(r => ({
      mainSegmentId: r.mainSegmentId,
      connectorCount: r.connectorIds.length,
      isValid: r.isValid
    }))
  });
  
  return relationships;
}

/**
 * Validates that extrusion relationships are intact after an operation
 */
export function validateExtrusionIntegrity(
  originalWalls: Wall[],
  updatedWalls: Wall[]
): { isValid: boolean; issues: string[]; brokenRelationships: string[] } {
  const issues: string[] = [];
  const brokenRelationships: string[] = [];
  
  const originalRelationships = analyzeExtrusionRelationships(originalWalls);
  const updatedRelationships = analyzeExtrusionRelationships(updatedWalls);
  
  // Check for missing main segments
  for (const originalRel of originalRelationships) {
    const updatedRel = updatedRelationships.find(r => r.mainSegmentId === originalRel.mainSegmentId);
    
    if (!updatedRel) {
      issues.push(`Main segment ${originalRel.mainSegmentId} was lost`);
      brokenRelationships.push(originalRel.mainSegmentId);
      continue;
    }
    
    // Check for missing connectors
    const missingConnectors = originalRel.connectorIds.filter(
      connId => !updatedRel.connectorIds.includes(connId)
    );
    
    if (missingConnectors.length > 0) {
      issues.push(`Connectors lost from ${originalRel.mainSegmentId}: ${missingConnectors.join(', ')}`);
      brokenRelationships.push(originalRel.mainSegmentId);
    }
    
    // Check if relationship is invalid after repair (regardless of original state)
    if (!updatedRel.isValid) {
      issues.push(`Extrusion ${originalRel.mainSegmentId} remains invalid after repair (insufficient connectors: ${updatedRel.connectorIds.length})`);
      brokenRelationships.push(originalRel.mainSegmentId);
    }
  }
  
  const isValid = issues.length === 0;
  
  if (!isValid) {
    console.error('🚨 EXTRUSION_INTEGRITY_VALIDATION_FAILED', {
      issuesCount: issues.length,
      brokenRelationshipsCount: brokenRelationships.length,
      issues,
      brokenRelationships
    });
  } else {
    console.log('✅ EXTRUSION_INTEGRITY_VALIDATED', {
      originalRelationships: originalRelationships.length,
      updatedRelationships: updatedRelationships.length
    });
  }
  
  return { isValid, issues, brokenRelationships };
}

/**
 * Updates a wall while preserving its extrusion metadata
 */
export function updateWallWithExtrusionMetadata(
  wall: Wall,
  updates: Partial<Wall>
): Wall {
  const originalMetadata = extractExtrusionMetadata(wall);
  const updatedWall = { ...wall, ...updates };
  
  return preserveExtrusionMetadata(updatedWall, originalMetadata);
}

/**
 * Repairs broken extrusion relationships by restoring missing metadata
 */
export function repairExtrusionRelationships(
  walls: Wall[],
  brokenRelationships: string[]
): Wall[] {
  if (brokenRelationships.length === 0) return walls;
  
  const repairedWalls = [...walls];
  
  for (const mainSegmentId of brokenRelationships) {
    const mainSegment = repairedWalls.find(w => (w as any).tempId === mainSegmentId);
    if (!mainSegment) {
      console.log(`🚨 REPAIR_DEBUG: Main segment ${mainSegmentId} not found`);
      continue;
    }
    
    console.log(`🔧 REPAIR_DEBUG: Processing main segment ${mainSegmentId}`, {
      mainSegmentTempId: (mainSegment as any).tempId,
      mainSegmentParentWallId: (mainSegment as any).parentWallId,
      startPoint: mainSegment.start,
      endPoint: mainSegment.end
    });
    
    // Find potential connectors using multiple strategies for 100% success
    const allConnectors = repairedWalls.filter(w => (w as any).isExtrusionConnector);
    console.log(`🔧 REPAIR_DEBUG: Found ${allConnectors.length} total connectors`);
    
    // Strategy 1: Geometric connection (increased tolerance for precision issues)
    const geometricConnectors = repairedWalls.filter(w => {
      const wallAny = w as any;
      if (!wallAny.isExtrusionConnector) return false;
      
      // Increased tolerance to 5 pixels to handle coordinate drift
      const tolerance = 5;
      const connectsToStart = 
        Math.abs(w.start.x - mainSegment.start.x) < tolerance && Math.abs(w.start.y - mainSegment.start.y) < tolerance ||
        Math.abs(w.end.x - mainSegment.start.x) < tolerance && Math.abs(w.end.y - mainSegment.start.y) < tolerance;
        
      const connectsToEnd = 
        Math.abs(w.start.x - mainSegment.end.x) < tolerance && Math.abs(w.start.y - mainSegment.end.y) < tolerance ||
        Math.abs(w.end.x - mainSegment.end.x) < tolerance && Math.abs(w.end.y - mainSegment.end.y) < tolerance;
      
      return connectsToStart || connectsToEnd;
    });
    
    // Strategy 2: Parent relationship (connectors with same parentWallId)
    const parentRelatedConnectors = repairedWalls.filter(w => {
      const wallAny = w as any;
      return wallAny.isExtrusionConnector && 
             wallAny.parentWallId === (mainSegment as any).parentWallId;
    });
    
    // Strategy 3: Orphaned connectors near this segment (within reasonable distance)
    const orphanedConnectors = repairedWalls.filter(w => {
      const wallAny = w as any;
      if (!wallAny.isExtrusionConnector) return false;
      if (wallAny.mainSegmentId) return false; // Already has a main segment
      
      // Check if connector is within reasonable distance (50 pixels) of main segment
      const maxDistance = 50;
      const distToStart = Math.sqrt(
        Math.pow(w.start.x - mainSegment.start.x, 2) + Math.pow(w.start.y - mainSegment.start.y, 2)
      );
      const distToEnd = Math.sqrt(
        Math.pow(w.start.x - mainSegment.end.x, 2) + Math.pow(w.start.y - mainSegment.end.y, 2)
      );
      const distToStartEnd = Math.sqrt(
        Math.pow(w.end.x - mainSegment.start.x, 2) + Math.pow(w.end.y - mainSegment.start.y, 2)
      );
      const distToEndEnd = Math.sqrt(
        Math.pow(w.end.x - mainSegment.end.x, 2) + Math.pow(w.end.y - mainSegment.end.y, 2)
      );
      
      return Math.min(distToStart, distToEnd, distToStartEnd, distToEndEnd) < maxDistance;
    });
    
    // Strategy 4: Connector flag reconstruction (nearby walls that should be connectors but lost their flag)
    const potentialConnectorsByGeometry = repairedWalls.filter(w => {
      const wallAny = w as any;
      if (wallAny.isExtrusionConnector) return false; // Already flagged
      if (wallAny.isMainExtrusionSegment) return false; // Is main segment
      
      // Check if this wall could be a missing connector based on proximity and geometry
      const maxDistance = 15; // Stricter tolerance for geometry reconstruction
      const distToStart = Math.sqrt(
        Math.pow(w.start.x - mainSegment.start.x, 2) + Math.pow(w.start.y - mainSegment.start.y, 2)
      );
      const distToEnd = Math.sqrt(
        Math.pow(w.start.x - mainSegment.end.x, 2) + Math.pow(w.start.y - mainSegment.end.y, 2)
      );
      const distToStartEnd = Math.sqrt(
        Math.pow(w.end.x - mainSegment.start.x, 2) + Math.pow(w.end.y - mainSegment.start.y, 2)
      );
      const distToEndEnd = Math.sqrt(
        Math.pow(w.end.x - mainSegment.end.x, 2) + Math.pow(w.end.y - mainSegment.end.y, 2)
      );
      
      const minDistance = Math.min(distToStart, distToEnd, distToStartEnd, distToEndEnd);
      const isNearby = minDistance < maxDistance;
      
      // Additional check: should be perpendicular-ish to main segment (extrusion connector pattern)
      const mainSegmentAngle = Math.atan2(mainSegment.end.y - mainSegment.start.y, mainSegment.end.x - mainSegment.start.x);
      const wallAngle = Math.atan2(w.end.y - w.start.y, w.end.x - w.start.x);
      const angleDiff = Math.abs(mainSegmentAngle - wallAngle);
      const isPerpendicular = angleDiff > Math.PI * 0.3 && angleDiff < Math.PI * 0.7; // Roughly perpendicular
      
      if (isNearby && isPerpendicular) {
        console.log(`🔧 ADVANCED_REPAIR: Found potential missing connector by geometry`, {
          wallId: wallAny.tempId,
          distance: minDistance.toFixed(1) + 'px',
          angleDiff: (angleDiff * 180 / Math.PI).toFixed(1) + '°',
          coords: `${w.start.x},${w.start.y} to ${w.end.x},${w.end.y}`
        });
      }
      
      return isNearby && isPerpendicular;
    });
    
    // Strategy 5: Wrong relationship connectors (connectors with incorrect mainSegmentId/parentWallId)
    const wrongRelationshipConnectors = repairedWalls.filter(w => {
      const wallAny = w as any;
      if (!wallAny.isExtrusionConnector) return false;
      
      const hasWrongMainSegmentId = wallAny.mainSegmentId && wallAny.mainSegmentId !== mainSegmentId;
      const hasWrongParentWallId = wallAny.parentWallId && wallAny.parentWallId !== (mainSegment as any).parentWallId && wallAny.parentWallId !== mainSegmentId;
      
      if (hasWrongMainSegmentId || hasWrongParentWallId) {
        // Check if this connector is actually closer to our main segment than its current assignment
        const distanceToOurMain = Math.min(
          Math.sqrt(Math.pow(w.start.x - mainSegment.start.x, 2) + Math.pow(w.start.y - mainSegment.start.y, 2)),
          Math.sqrt(Math.pow(w.start.x - mainSegment.end.x, 2) + Math.pow(w.start.y - mainSegment.end.y, 2)),
          Math.sqrt(Math.pow(w.end.x - mainSegment.start.x, 2) + Math.pow(w.end.y - mainSegment.start.y, 2)),
          Math.sqrt(Math.pow(w.end.x - mainSegment.end.x, 2) + Math.pow(w.end.y - mainSegment.end.y, 2))
        );
        
        // Only reassign if it's very close (15px) - avoid stealing connectors from distant segments
        if (distanceToOurMain < 15) {
          console.log(`🔧 ADVANCED_REPAIR: Found misassigned connector`, {
            connectorId: wallAny.tempId,
            currentMainSegmentId: wallAny.mainSegmentId,
            currentParentWallId: wallAny.parentWallId,
            distanceToOurMain: distanceToOurMain.toFixed(1) + 'px',
            ourMainSegmentId: mainSegmentId
          });
          return true;
        }
      }
      
      return false;
    });
    
    // Combine all strategies (remove duplicates)
    const allPotentialConnectors = [
      ...geometricConnectors,
      ...parentRelatedConnectors,
      ...orphanedConnectors,
      ...potentialConnectorsByGeometry,
      ...wrongRelationshipConnectors
    ];
    const uniqueConnectors = allPotentialConnectors.filter((connector, index, arr) => 
      arr.findIndex(c => (c as any).tempId === (connector as any).tempId) === index
    );
    
    console.log(`🔧 REPAIR_DEBUG: Found connectors via strategies`, {
      mainSegmentId,
      geometric: geometricConnectors.length,
      parentRelated: parentRelatedConnectors.length,
      orphaned: orphanedConnectors.length,
      geometryReconstruction: potentialConnectorsByGeometry.length,
      wrongRelationships: wrongRelationshipConnectors.length,
      totalUnique: uniqueConnectors.length
    });
    
    // Log details about found connectors
    for (const connector of uniqueConnectors) {
      const wallAny = connector as any;
      console.log(`🔧 REPAIR_DEBUG: Found matching connector`, {
        connectorId: wallAny.tempId,
        connectorStart: connector.start,
        connectorEnd: connector.end,
        existingMainSegmentId: wallAny.mainSegmentId,
        existingParentWallId: wallAny.parentWallId
      });
    }
    
    // Repair connector metadata - ensure connectors have IDs first
    let repairedCount = 0;
    let reconstructedCount = 0;
    
    for (const connector of uniqueConnectors) {
      const connectorIndex = repairedWalls.indexOf(connector);
      if (connectorIndex !== -1) {
        const connectorAny = connector as any;
        const wasNotConnector = !connectorAny.isExtrusionConnector;
        
        // Ensure connector has a tempId (generate one if missing)
        if (!connectorAny.tempId) {
          connectorAny.tempId = `connector-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
          console.log('🔧 REPAIR_DEBUG: Generated tempId for connector', {
            generatedId: connectorAny.tempId,
            connectorStart: connector.start,
            connectorEnd: connector.end
          });
        }
        
        repairedWalls[connectorIndex] = {
          ...connector,
          mainSegmentId,
          parentWallId: (mainSegment as any).parentWallId || mainSegmentId,
          isExtrusionConnector: true // Ensure flag is set (critical for reconstruction)
        } as any;
        
        repairedCount++;
        if (wasNotConnector) {
          reconstructedCount++;
          console.log('🔧 ADVANCED_REPAIR: RECONSTRUCTED connector from regular wall', {
            wallId: connectorAny.tempId,
            mainSegmentId,
            parentWallId: (mainSegment as any).parentWallId || mainSegmentId,
            coords: `${connector.start.x},${connector.start.y} to ${connector.end.x},${connector.end.y}`
          });
        } else {
          console.log('🔧 REPAIR_DEBUG: Updated existing connector metadata', {
            connectorId: connectorAny.tempId,
            mainSegmentId,
            parentWallId: (mainSegment as any).parentWallId || mainSegmentId
          });
        }
      }
    }
    
    console.log('🔧 REPAIRED_EXTRUSION_RELATIONSHIP', {
      mainSegmentId,
      repairedConnectors: repairedCount,
      reconstructedConnectors: reconstructedCount,
      uniqueConnectorsFound: uniqueConnectors.length,
      strategiesUsed: {
        geometric: geometricConnectors.length,
        parentRelated: parentRelatedConnectors.length,
        orphaned: orphanedConnectors.length,
        geometryReconstruction: potentialConnectorsByGeometry.length,
        wrongRelationships: wrongRelationshipConnectors.length
      }
    });
  }
  
  return repairedWalls;
}
